import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layouts from '../../layouts/Layouts';
import useCabang from '../features/useCabang';
import useKarat from '../features/useKarat';
import useBaki from '../features/useBaki';
import SuratJalanPrint from '../../components/SuratJalanPrint';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { imageBaseUrl } from '../../config/http';
import {
  IconTruckDelivery,
  IconSearch,
  IconX,
  IconPrinter,
  IconCalendarTime,
  IconChevronDown,
  IconChevronUp,
  IconEye,
  IconPhoto,
  IconChartPie,
  IconListDetails,
  IconInbox,
  IconBolt,
  IconLoader2,
  IconReload
} from '@tabler/icons-react';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css'; 
import 'react-date-range/dist/theme/default.css';
import moment from 'moment';

const CreateSuratJalan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cabang } = useCabang(); // Toko branches
  const { karatOption } = useKarat();
  const { bakiOption } = useBaki();

  const [cabangTujuanId, setCabangTujuanId] = useState('');
  const [namaKurir, setNamaKurir] = useState('');
  const [catatanPengiriman, setCatatanPengiriman] = useState('');

  const [brankasItems, setBrankasItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingType, setSubmittingType] = useState(null); // 'standar' | 'tanpa_verifikasi' | null

  // Server-Side Pagination & Search State
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [totalBeratKeseluruhan, setTotalBeratKeseluruhan] = useState(0);

  // Filter States
  const [kategoriList, setKategoriList] = useState([]);
  const [selectedKategori, setSelectedKategori] = useState('');
  const [selectedJenisKategori, setSelectedJenisKategori] = useState('');
  const [selectedBaki, setSelectedBaki] = useState('');
  const [selectedKarat, setSelectedKarat] = useState([]);
  const [filterBerat, setFilterBerat] = useState('');
  const [isOpenJenisKategoriModal, setIsOpenJenisKategoriModal] = useState(false);
  const [searchJenisKategori, setSearchJenisKategori] = useState('');
  
  const [scanInput, setScanInput] = useState('');
  const searchInputRef = useRef(null);
  const scanTimeoutRef = useRef(null);
  
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const selectionRange = {
    startDate: startDate || new Date(),
    endDate: endDate || new Date(),
    key: 'selection',
  };

  const handleSelectDate = (date) => {
    setStartDate(date.selection.startDate);
    setEndDate(date.selection.endDate);
    setCurrentPage(1);
  };

  const awal = startDate ? moment(startDate).format('DD-MM-YYYY') : '';
  const akhir = endDate ? moment(endDate).format('DD-MM-YYYY') : '';

  // Selected items map across pages: { [id]: itemObject }
  const [selectedItemsMap, setSelectedItemsMap] = useState(() => {
    const initialMap = {};
    if (location.state?.preSelectedItems && Array.isArray(location.state.preSelectedItems)) {
      location.state.preSelectedItems.forEach((item) => {
        if (item && item.id) {
          initialMap[item.id] = item;
        }
      });
    }
    return initialMap;
  });

  useEffect(() => {
    if (location.state?.preSelectedItems && Array.isArray(location.state.preSelectedItems) && location.state.preSelectedItems.length > 0) {
      toast.info(`${location.state.preSelectedItems.length} barang dari List Brankas dimuat ke pengiriman`, { autoClose: 2000 });
    }
  }, [location.state]);

  // Card Collapsible States
  const [showDetailPengiriman, setShowDetailPengiriman] = useState(true);
  const [showRingkasanBarang, setShowRingkasanBarang] = useState(true);

  // Modal Print State
  const [createdData, setCreatedData] = useState(null);
  const [createdBreakdown, setCreatedBreakdown] = useState([]);
  const [isOpenPrint, setIsOpenPrint] = useState(false);
  
  // Detail Modal State (Mirip modal Detail Permohonan)
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  const [modalTab, setModalTab] = useState('ringkasan');
  const [searchModalItem, setSearchModalItem] = useState('');

  // Confirm Modal State
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const token = localStorage.getItem('token');

  // Fetch Kategori Data
  const fetchKategori = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/kategori`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (Array.isArray(res.data)) {
        setKategoriList(res.data);
      } else if (res.data.status && Array.isArray(res.data.data)) {
        setKategoriList(res.data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const role = localStorage.getItem('role') || user?.role;
    if (role === 'operator_rumah') {
      toast.error('Akses ditolak: Operator rumah tidak diizinkan mengakses menu pengiriman');
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    fetchKategori();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch items in Brankas Toko Pusat (ready to ship) with server-side pagination & search
  const getBrankasItems = async () => {
    setLoadingItems(true);
    try {
      let url = `${process.env.REACT_APP_BASE_URL}/item/brankas?lokasi=pusat&for_pengiriman=1&status_pengiriman=ready&page=${currentPage}&per_page=${perPage}`;
      if (searchQuery) url += `&kode=${encodeURIComponent(searchQuery)}`;
      if (selectedKategori) url += `&kategori_id=${selectedKategori}`;
      if (selectedJenisKategori) url += `&jenis_kategori_id=${selectedJenisKategori}`;
      if (selectedBaki) url += `&nomor_rak=${selectedBaki}`;
      if (selectedKarat.length > 0) url += `&karat[]=${selectedKarat.join('&karat[]=')}`;
      if (filterBerat) url += `&berat=${filterBerat}`;
      if (startDate && endDate) {
        url += `&start_date=${moment(startDate).format('YYYY-MM-DD')}&end_date=${moment(endDate).format('YYYY-MM-DD')}&tgl_awal=${awal}&tgl_akhir=${akhir}`;
      }

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.status) {
        const paginator = res.data.data;
        const items = paginator.data || [];
        setBrankasItems(items);
        setTotalItems(paginator.total || items.length);
        setLastPage(paginator.last_page || 1);
        setTotalBeratKeseluruhan(res.data.total_berat_filter ?? res.data.total_berat ?? 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    getBrankasItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, searchQuery, selectedKategori, selectedJenisKategori, selectedBaki, selectedKarat, startDate, endDate]);

  const handleScanSearch = useCallback(async (kode) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/item/brankas?lokasi=pusat&for_pengiriman=1&status_pengiriman=ready&kode=${kode}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status && res.data.data && res.data.data.data && res.data.data.data.length > 0) {
        const item = res.data.data.data[0];
        if (!selectedItemsMap[item.id]) {
          setSelectedItemsMap(prev => ({ ...prev, [item.id]: item }));
          toast.success(`${item.kode_item} berhasil ditambahkan ✓`, { autoClose: 1500 });
        }
      } else {
        toast.error(`Barang dengan kode "${kode}" tidak ditemukan di Brankas Toko Pusat (Ready)`, { autoClose: 2000 });
      }
    } catch (err) {
      toast.error('Gagal mencari barang scan');
    } finally {
      setScanInput('');
      if (searchInputRef.current) searchInputRef.current.focus();
    }
  }, [selectedItemsMap, token]);

  useEffect(() => {
    if (scanInput.length >= 10) {
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = setTimeout(() => {
        setSearchQuery(scanInput);
        handleScanSearch(scanInput);
      }, 300);
    }
    return () => {
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
    };
  }, [scanInput, handleScanSearch]);

  const handleToggleSelectItem = (item) => {
    const newMap = { ...selectedItemsMap };
    if (newMap[item.id]) {
      delete newMap[item.id];
    } else {
      newMap[item.id] = item;
    }
    setSelectedItemsMap(newMap);
  };

  const handleSelectAllOnPage = () => {
    const pageItemIds = brankasItems.map((i) => i.id);
    const allPageSelected = pageItemIds.every((id) => !!selectedItemsMap[id]);

    const newMap = { ...selectedItemsMap };
    if (allPageSelected) {
      pageItemIds.forEach((id) => delete newMap[id]);
    } else {
      brankasItems.forEach((item) => {
        newMap[item.id] = item;
      });
    }
    setSelectedItemsMap(newMap);
  };

  const handleResetAndReload = async () => {
    setSelectedKategori('');
    setSelectedJenisKategori('');
    setSelectedBaki('');
    setSelectedKarat([]);
    setFilterBerat('');
    setScanInput('');
    setSearchQuery('');
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);

    setLoadingItems(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/item/brankas?lokasi=pusat&for_pengiriman=1&status_pengiriman=ready&page=1&per_page=${perPage}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data?.status) {
        const paginator = res.data.data;
        const items = paginator.data || [];
        setBrankasItems(items);
        setTotalItems(paginator.total || items.length);
        setLastPage(paginator.last_page || 1);
        setTotalBeratKeseluruhan(res.data.total_berat_filter ?? res.data.total_berat ?? 0);
        toast.info('Filter di-reset & data Brankas Toko Pusat diperbarui', { autoClose: 1200 });
      }
    } catch (err) {
      console.error('Gagal reload data brankas:', err);
      toast.error('Gagal memuat ulang data brankas');
    } finally {
      setLoadingItems(false);
    }
  };

  const selectedItemsList = Object.values(selectedItemsMap);
  const selectedItemIds = Object.keys(selectedItemsMap).map(Number);

  const breakdownCategory = selectedItemsList.reduce((acc, item) => {
    const katName = item.jenis_kategori?.kategori?.nama_kategori || 'Lainnya';
    if (!acc[katName]) {
      acc[katName] = { name: katName, pcs: 0, berat: 0 };
    }
    acc[katName].pcs += 1;
    acc[katName].berat += parseFloat(item.berat || 0);
    return acc;
  }, {});

  const totalPcs = selectedItemsList.length;
  const totalBerat = selectedItemsList.reduce((acc, i) => acc + parseFloat(i.berat || 0), 0);

  // Memoized Detail Summary for Modal (Grouped by Category & Karat)
  const detailSummary = useMemo(() => {
    return selectedItemsList.reduce((acc, item) => {
      const catName = item.jenis_kategori?.kategori?.nama_kategori || 'Tanpa Kategori';
      const karat = item.karat || '-';
      const berat = parseFloat(item.berat || 0);

      if (!acc[catName]) {
        acc[catName] = { name: catName, pcs: 0, berat: 0, byKarat: {} };
      }

      acc[catName].pcs += 1;
      acc[catName].berat += berat;

      if (!acc[catName].byKarat[karat]) {
        acc[catName].byKarat[karat] = { pcs: 0, berat: 0 };
      }

      acc[catName].byKarat[karat].pcs += 1;
      acc[catName].byKarat[karat].berat += berat;

      return acc;
    }, {});
  }, [selectedItemsList]);

  // Image helper for gallery & modal
  const resolveItemImageUrl = (item) => {
    if (!item) return '';
    let gData = item.gambar1 || item.gambar;
    if (typeof gData === 'string' && (gData.startsWith('[') || gData.startsWith('{'))) {
      try {
        gData = JSON.parse(gData);
      } catch (e) {}
    }
    if (Array.isArray(gData) && gData.length > 0) {
      const path = gData[0]?.asli || gData[0]?.thumbnail || (typeof gData[0] === 'string' ? gData[0] : '');
      return path ? (path.startsWith('http') ? path : `${imageBaseUrl}${path}`) : '';
    }
    if (typeof gData === 'string' && gData.length > 0) {
      return gData.startsWith('http') ? gData : `${imageBaseUrl}${gData}`;
    }
    return '';
  };

  // Filtered Items inside Detail Modal Search
  const filteredModalItems = useMemo(() => {
    if (!selectedItemsList || selectedItemsList.length === 0) return [];
    if (!searchModalItem.trim()) return selectedItemsList;
    const q = searchModalItem.toLowerCase();
    return selectedItemsList.filter((it) => {
      const kode = it.kode_item ? it.kode_item.toLowerCase() : '';
      const jenis = it.jenis_kategori?.nama_jenis ? it.jenis_kategori.nama_jenis.toLowerCase() : '';
      const kat = it.jenis_kategori?.kategori?.nama_kategori ? it.jenis_kategori.kategori.nama_kategori.toLowerCase() : '';
      const baki = it.nomor_rak ? it.nomor_rak.toString().toLowerCase() : '';
      return kode.includes(q) || jenis.includes(q) || kat.includes(q) || baki.includes(q);
    });
  }, [selectedItemsList, searchModalItem]);

  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (!cabangTujuanId) {
      toast.warning('Silakan pilih Toko Tujuan pengiriman!');
      return;
    }
    if (!namaKurir.trim()) {
      toast.warning('Silakan isi nama kurir / pengantar barang!');
      return;
    }
    if (selectedItemIds.length === 0) {
      toast.warning('Pilih minimal 1 barang dari Brankas untuk dikirim!');
      return;
    }
    setIsConfirmOpen(true);
  };

  const handleSubmitSuratJalan = async (tanpaVerifikasi = false) => {
    setIsSubmitting(true);
    setSubmittingType(tanpaVerifikasi ? 'tanpa_verifikasi' : 'standar');
    try {
      const payload = {
        cabang_asal_id: 4,
        cabang_tujuan_id: cabangTujuanId,
        nama_kurir: namaKurir,
        items: selectedItemIds,
        catatan_pengiriman: catatanPengiriman,
        tanpa_verifikasi: tanpaVerifikasi
      };

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/pengiriman`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status) {
        let detailData = res.data.data;
        let breakdown = res.data.breakdown_kategori || [];

        // Fallback jika breakdown_kategori tidak tersedia di respons utama
        if (!breakdown || breakdown.length === 0) {
          try {
            const detailRes = await axios.get(
              `${process.env.REACT_APP_BASE_URL}/pengiriman/${res.data.data.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            detailData = detailRes.data.data;
            breakdown = detailRes.data.breakdown_kategori || [];
          } catch (e) {
            console.warn('Fallback detail fetch error:', e);
          }
        }

        setIsConfirmOpen(false);
        setCreatedData(detailData);
        setCreatedBreakdown(breakdown);
        setIsOpenPrint(true);

        if (tanpaVerifikasi) {
          toast.success(`Surat Jalan ${res.data.data.no_surat_jalan} berhasil dibuat & langsung disetujui (tanpa verifikasi)!`);
        } else {
          toast.success(`Surat Jalan ${res.data.data.no_surat_jalan} berhasil dibuat!`);
        }

        // Reset form
        setSelectedItemsMap({});
        setNamaKurir('');
        setCatatanPengiriman('');
        getBrankasItems();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Gagal membuat Surat Jalan');
    } finally {
      setIsSubmitting(false);
      setSubmittingType(null);
    }
  };

  return (
    <Layouts title="Buat Pengiriman (Brankas -> Toko)">
      <div className="w-full p-4 sm:p-6 space-y-6">
        
        {/* Header Card */}
        {/* <div className="bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-md">
              <IconTruckDelivery size={32} />
            </div>
            <div>
              <h1 className="text-xl font-bold">Buat Surat Jalan & Delivery Order</h1>
              <p className="text-xs text-amber-100 mt-0.5">
                Pengiriman barang resmi dari Brankas Pusat (Rumah) ke Toko Tujuan via Kurir.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/histori-pengiriman')}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg border border-white/20 transition-all"
          >
            Lihat Histori Pengiriman
          </button>
        </div> */}

        {/* Main Grid: Form Left, Items Table Right */}
        <form onSubmit={handlePreSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Left Column: Form Details (3 Cols) */}
          <div className="lg:col-span-3 space-y-5">
            
            {/* Form Card */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 flex items-center gap-2">
                  <IconTruckDelivery size={18} className="text-amber-500" />
                  <span>Detail Pengiriman</span>
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate('/histori-pengiriman')}
                    className="text-[11px] font-semibold text-amber-600 hover:text-amber-700 dark:text-amber-400 hover:underline"
                  >
                    Histori →
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDetailPengiriman(!showDetailPengiriman)}
                    className="p-1 text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-lg transition-all"
                    title={showDetailPengiriman ? "Sembunyikan Detail Pengiriman" : "Tampilkan Detail Pengiriman"}
                  >
                    {showDetailPengiriman ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                  </button>
                </div>
              </div>

              {/* Collapsed Preview */}
              {!showDetailPengiriman && (
                <div className="py-1 text-xs text-slate-600 dark:text-slate-300 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Tujuan:</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[150px]">
                      {cabang.find(c => String(c.id) === String(cabangTujuanId))?.nama_cabang || <span className="text-amber-500 italic">Belum dipilih</span>}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Kurir:</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[150px]">
                      {namaKurir || <span className="text-amber-500 italic">Belum diisi</span>}
                    </span>
                  </div>
                </div>
              )}

              {/* Expanded Form Content */}
              {showDetailPengiriman && (
                <div className="space-y-4">
                  {/* Asal Pengiriman (Terkunci ke Brankas Toko Pusat) */}
                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">
                      Asal Pengiriman
                    </label>
                    <div className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm border border-amber-200 dark:border-amber-900/50 rounded-xl bg-amber-50/60 dark:bg-amber-950/20 text-slate-800 dark:text-slate-100 font-semibold">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                      <span>Brankas Toko Pusat (Rumah)</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">
                      Toko Tujuan <span className="text-rose-500">*</span>
                    </label>
                    <select
                      value={cabangTujuanId}
                      onChange={(e) => setCabangTujuanId(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 text-sm border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">-- Pilih Toko Tujuan --</option>
                      {cabang.filter(c => c.is_toko !== false).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nama_cabang} ({c.lokasi})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">
                      Nama Kurir / Pengantar <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Contoh: Pak Andi / Pak Budi"
                      value={namaKurir}
                      onChange={(e) => setNamaKurir(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 text-sm border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">
                      Catatan Pengiriman (Opsional)
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Catatan tambahan..."
                      value={catatanPengiriman}
                      onChange={(e) => setCatatanPengiriman(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Summary Card */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300">
                  Ringkasan Barang Dikeluarkan
                </h3>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setIsOpenDetailModal(true)}
                    disabled={totalPcs === 0}
                    className="p-1 px-2 text-[11px] font-bold text-amber-800 dark:text-amber-300 hover:bg-amber-500/20 rounded-lg transition-all flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    title="Buka Modal Detail Barang Dikeluarkan"
                  >
                    <IconEye size={15} />
                    <span>Detail</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRingkasanBarang(!showRingkasanBarang)}
                    className="p-1 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 rounded-lg transition-all"
                    title={showRingkasanBarang ? "Sembunyikan Ringkasan Barang" : "Tampilkan Ringkasan Barang"}
                  >
                    {showRingkasanBarang ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                  </button>
                </div>
              </div>

              {/* Collapsed Preview */}
              {!showRingkasanBarang && (
                <div className="space-y-3 pt-1">
                  <div className="flex justify-between items-center text-xs font-bold text-amber-900 dark:text-amber-200 bg-amber-500/10 px-3 py-2 rounded-xl">
                    <span>{totalPcs} Pcs</span>
                    <span>{totalBerat.toFixed(3)} gr</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setIsOpenDetailModal(true)}
                      disabled={totalPcs === 0}
                      className="py-2 px-2 rounded-xl font-bold text-xs bg-white dark:bg-slate-800 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-slate-700 hover:bg-amber-50 dark:hover:bg-slate-700 transition-all flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <IconEye size={15} />
                      <span>Detail</span>
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || totalPcs === 0}
                      className={`py-2 px-2 rounded-xl font-bold text-xs shadow-md transition-all flex items-center justify-center gap-1 ${
                        isSubmitting || totalPcs === 0
                          ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-amber-500/25'
                      }`}
                    >
                      <IconPrinter size={15} />
                      <span>Kirim</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Expanded Content */}
              {showRingkasanBarang && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-amber-200 dark:border-slate-700">
                      <span className="text-[11px] text-slate-500 block">Total Jumlah:</span>
                      <span className="text-lg font-extrabold text-slate-800 dark:text-slate-100">{totalPcs} <span className="text-xs font-normal">Pcs</span></span>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-amber-200 dark:border-slate-700">
                      <span className="text-[11px] text-slate-500 block">Total Berat:</span>
                      <span className="text-lg font-extrabold text-amber-600 dark:text-amber-400">{totalBerat.toFixed(3)} <span className="text-xs font-normal">gr</span></span>
                    </div>
                  </div>

                  {/* Breakdown Category Summary */}
                  {Object.keys(breakdownCategory).length > 0 && (
                    <div className="space-y-1.5 pt-2 border-t border-amber-200/60 dark:border-slate-700">
                      <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 block">Rincian per Kategori:</span>
                      {Object.values(breakdownCategory).map((b, i) => (
                        <div key={i} className="flex justify-between items-center text-xs bg-white/70 dark:bg-slate-800/70 px-2.5 py-1.5 rounded-lg border border-amber-100 dark:border-slate-700">
                          <span className="font-medium text-slate-700 dark:text-slate-300">{b.name}</span>
                          <span className="font-bold text-slate-900 dark:text-white">{b.pcs} pcs ({b.berat.toFixed(3)}g)</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Tombol Lihat Detail Modal */}
                  <button
                    type="button"
                    onClick={() => setIsOpenDetailModal(true)}
                    disabled={totalPcs === 0}
                    className="w-full py-2.5 px-3 rounded-xl font-bold text-xs bg-white dark:bg-slate-800 text-amber-800 dark:text-amber-300 border border-amber-300/80 dark:border-amber-700/50 hover:bg-amber-50 dark:hover:bg-slate-700/50 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    <IconListDetails size={16} className="text-amber-600 dark:text-amber-400" />
                    <span>Lihat Detail Modal ({totalPcs} Barang)</span>
                  </button>

                  <button
                    type="submit"
                    disabled={isSubmitting || totalPcs === 0}
                    className={`w-full py-3.5 px-4 rounded-xl font-bold text-sm shadow-lg transition-all flex items-center justify-center gap-2 ${
                      isSubmitting || totalPcs === 0
                        ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-amber-500/25'
                    }`}
                  >
                    <IconPrinter size={20} />
                    <span>{isSubmitting ? 'Memproses...' : 'Buat Surat Jalan & Cetak'}</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Right Column: Brankas Item Selector Table (9 Cols) */}
          <div className="lg:col-span-9 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm flex flex-col space-y-4">
            
            {/* Table Header Controls (Filters) */}
            <div className="flex flex-col gap-3 border-b border-slate-100 dark:border-slate-700 pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <span className="font-bold text-sm text-slate-800 dark:text-slate-100">
                    Daftar Barang Brankas Toko Pusat
                  </span>
                  <button
                    type="button"
                    onClick={handleResetAndReload}
                    title="Reload / Refresh (Reset Filter ke Default)"
                    className="p-1.5 text-slate-500 hover:text-amber-600 dark:text-slate-400 dark:hover:text-amber-400 hover:bg-slate-100 dark:hover:bg-slate-700/60 rounded-xl transition-all cursor-pointer border border-slate-200 dark:border-slate-700 shadow-xs flex items-center justify-center group"
                  >
                    <IconReload size={16} className={`transition-transform duration-300 ${loadingItems ? 'animate-spin text-amber-500' : 'group-hover:rotate-180'}`} />
                  </button>
                </div>
                {selectedItemIds.length > 0 && (
                  <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-2.5 py-1 rounded-lg border border-emerald-200 dark:border-emerald-800/40">
                    ✓ {selectedItemIds.length} barang dipilih ({totalBerat.toFixed(3)} gr)
                  </span>
                )}
              </div>

              {/* Filters Row */}
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {/* Kategori Select */}
                <select
                  value={selectedKategori}
                  onChange={(e) => {
                    setSelectedKategori(e.target.value);
                    setSelectedJenisKategori('');
                    setCurrentPage(1);
                  }}
                  className="border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-1.5 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700 cursor-pointer outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Semua Kategori</option>
                  {kategoriList.map(k => (
                    <option key={k.id} value={k.id}>{k.nama_kategori}</option>
                  ))}
                </select>

                {/* Jenis Kategori Modal Trigger */}
                {selectedKategori && (
                  <button
                    type="button"
                    onClick={() => setIsOpenJenisKategoriModal(true)}
                    className="border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-1.5 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700 hover:bg-slate-50 transition-all text-left truncate max-w-[150px]"
                  >
                    {selectedJenisKategori 
                      ? (kategoriList.find(k => k.id === parseInt(selectedKategori))?.jenis_kategori?.find(j => j.id.toString() === selectedJenisKategori)?.nama_jenis || 'Jenis Kategori') 
                      : 'Pilih Jenis Kategori'}
                  </button>
                )}

                {/* Baki Select */}
                <select
                  value={selectedBaki}
                  onChange={(e) => { setSelectedBaki(e.target.value); setCurrentPage(1); }}
                  className="border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-1.5 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700 cursor-pointer outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Baki</option>
                  {bakiOption.map((bakiNum) => (
                    <option key={bakiNum} value={bakiNum}>{bakiNum}</option>
                  ))}
                </select>

                {/* Karat Checkbox Dropdown */}
                <div className="dropdown dropdown-bottom">
                  <div
                    tabIndex={0}
                    className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer hover:border-amber-400 hover:text-amber-500 transition-all duration-200 flex items-center gap-2 text-xs"
                  >
                    <span>
                      {selectedKarat.length > 0 ? `Karat (${selectedKarat.length})` : 'Karat'}
                    </span>
                  </div>
                  <ul tabIndex={0} className="dropdown-content menu p-2 shadow-xl bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 z-50 mt-1 w-48 max-h-60 overflow-y-auto">
                    {karatOption.map((k) => (
                      <li key={k}>
                        <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg">
                          <input
                            type="checkbox"
                            className="checkbox checkbox-xs checkbox-warning rounded"
                            checked={selectedKarat.includes(k.toString()) || selectedKarat.includes(k)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedKarat([...selectedKarat, k.toString()]);
                              } else {
                                setSelectedKarat(selectedKarat.filter(val => val !== k.toString()));
                              }
                              setCurrentPage(1);
                            }}
                          />
                          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{k} Karat</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Date Range Dropdown */}
                <div className="dropdown dropdown-bottom">
                  <div
                    tabIndex={0}
                    className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer hover:border-amber-400 hover:text-amber-500 transition-all duration-200 flex items-center gap-2 text-xs"
                  >
                    <IconCalendarTime size={16} />
                    <span>
                      {startDate && endDate ? (awal === akhir ? awal : `${awal} - ${akhir}`) : 'Semua Tanggal'}
                    </span>
                  </div>
                  <div tabIndex={0} className="dropdown-content menu p-2 shadow-xl bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 z-50 mt-1 right-0 sm:right-auto">
                    <DateRange ranges={[selectionRange]} onChange={handleSelectDate} />
                  </div>
                </div>

                {/* Cari Berat Input */}
                <div className="flex items-center gap-1.5 flex-1 min-w-[140px]">
                  <input
                    type="text"
                    placeholder="Cari berat (ex: 2.5)..."
                    value={filterBerat}
                    onChange={(e) => setFilterBerat(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setCurrentPage(1);
                      }
                    }}
                    className="w-full px-3 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(1);
                    }}
                    className="p-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl shadow-sm transition-colors"
                  >
                    <IconSearch size={16} />
                  </button>
                </div>

                {/* Search String Input */}
                <div className="relative flex-1 min-w-[180px]">
                  <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="search"
                    placeholder="Scan / Cari Kode Barang"
                    value={scanInput}
                    onChange={(e) => {
                      setScanInput(e.target.value);
                      if (e.target.value.length < 10) {
                        setSearchQuery(e.target.value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setCurrentPage(1);
                      }
                    }}
                    className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                {/* Reset & Reload Button */}
                <button
                  type="button"
                  onClick={handleResetAndReload}
                  title="Reload / Refresh (Reset Filter ke Default)"
                  className="px-2.5 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-400 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 hover:bg-amber-50 dark:hover:bg-slate-600 transition-all flex items-center gap-1.5 cursor-pointer shadow-xs group shrink-0"
                >
                  <IconReload size={15} className={`transition-transform duration-300 ${loadingItems ? 'animate-spin text-amber-500' : 'group-hover:rotate-180'}`} />
                  <span className="text-[11px]">Reload</span>
                </button>
              </div>
            </div>

            {/* Items Table */}
            <div className="flex-1 overflow-x-auto min-h-[400px]">
              {loadingItems ? (
                <div className="flex items-center justify-center py-20 text-slate-400 text-sm">
                  Memuat data Brankas Toko Pusat...
                </div>
              ) : totalItems === 0 ? (
                <div className="flex items-center justify-center py-20 text-slate-400 text-sm">
                  Tidak ada barang tersedia di Brankas Toko Pusat.
                </div>
              ) : (
                <>
                  <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                  <thead className="text-[11px] uppercase bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="p-3 text-center w-12">No</th>
                      <th className="p-3">Kode Barang</th>
                      <th className="p-3">Nama Barang</th>
                      <th className="p-3 text-center">Baki</th>
                      <th className="p-3 text-right">Karat | Berat</th>
                      <th className="p-3 text-center w-12">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-xs checkbox-warning rounded cursor-pointer"
                          checked={brankasItems.length > 0 && brankasItems.every(i => !!selectedItemsMap[i.id])}
                          onChange={handleSelectAllOnPage}
                          title="Pilih Semua Halaman Ini"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                    {brankasItems.map((item, index) => {
                      const isSelected = !!selectedItemsMap[item.id];

                      return (
                        <tr
                          key={item.id}
                          onClick={() => handleToggleSelectItem(item)}
                          className={`cursor-pointer transition-colors ${
                            isSelected ? 'bg-amber-50/80 dark:bg-amber-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'
                          }`}
                        >
                          <td className="p-3 text-center font-medium text-slate-500 dark:text-slate-400">
                            {(currentPage - 1) * perPage + index + 1}
                          </td>
                          <td className="p-3 font-mono font-bold text-slate-800 dark:text-slate-100">{item.kode_item}</td>
                          <td className="p-3">
                            <span className="font-semibold text-slate-700 dark:text-slate-200">{item.jenis_kategori?.nama_jenis}</span>
                          </td>
                          <td className="p-3 text-center">{item.nomor_rak || '-'}</td>
                          <td className="p-3 text-right font-bold text-amber-600">
                            {item.karat} K <span className="text-slate-400 mx-1">|</span> {parseFloat(item.berat).toFixed(3)} gr
                          </td>
                          <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="checkbox checkbox-xs checkbox-warning rounded cursor-pointer"
                              checked={isSelected}
                              onChange={() => handleToggleSelectItem(item)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {brankasItems.length > 0 && (
                    <tfoot className="bg-amber-50/60 dark:bg-slate-800/90 border-t-2 border-amber-200/80 dark:border-slate-700 text-xs font-bold text-slate-700 dark:text-slate-200">
                      <tr>
                        <td colSpan="4" className="p-3 text-right text-slate-600 dark:text-slate-300">
                          Total Berat Halaman Ini:
                        </td>
                        <td className="p-3 text-right font-extrabold text-amber-600 dark:text-amber-400 whitespace-nowrap">
                          {brankasItems.reduce((sum, item) => sum + parseFloat(item.berat || 0), 0).toFixed(3)} gr
                        </td>
                        <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-xs checkbox-warning rounded cursor-pointer"
                            checked={brankasItems.length > 0 && brankasItems.every(i => !!selectedItemsMap[i.id])}
                            onChange={handleSelectAllOnPage}
                            title="Pilih Semua Halaman Ini"
                          />
                        </td>
                      </tr>
                      {totalItems > brankasItems.length && (
                        <tr className="border-t border-amber-100 dark:border-slate-700/60 bg-amber-50/30 dark:bg-slate-800/40 text-[11px]">
                          <td colSpan="4" className="px-3 py-2 text-right text-slate-500 dark:text-slate-400 font-normal">
                            Total Berat Keseluruhan ({totalItems} barang):
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-slate-800 dark:text-slate-100 whitespace-nowrap">
                            {parseFloat(String(totalBeratKeseluruhan).replace(/,/g, '') || 0).toFixed(3)} gr
                          </td>
                          <td className="p-2"></td>
                        </tr>
                      )}
                    </tfoot>
                  )}
                </table>
                {brankasItems.length > 0 && (
                  <div className="flex items-center justify-between px-4 py-2.5 bg-amber-50/40 dark:bg-slate-800/80 border-t border-amber-200/60 dark:border-slate-700/60 text-xs text-slate-700 dark:text-slate-300">
                    <div className="flex items-center gap-2">
                      <input
                        id="checkbox-all-bottom"
                        type="checkbox"
                        checked={brankasItems.length > 0 && brankasItems.every(i => !!selectedItemsMap[i.id])}
                        onChange={handleSelectAllOnPage}
                        className="cursor-pointer checkbox checkbox-xs checkbox-warning rounded"
                      />
                      <label htmlFor="checkbox-all-bottom" className="font-semibold cursor-pointer select-none">
                        Pilih Semua Barang (Halaman Ini)
                      </label>
                    </div>
                    <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                      {selectedItemIds.length} dari {totalItems} barang dipilih
                    </span>
                  </div>
                )}
              </>
            )}
            </div>

            {/* Pagination Controls Footer */}
            {totalItems > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-100 dark:border-slate-700 text-xs">
                <div className="flex items-center gap-2 text-slate-500">
                  <span>Tampilkan</span>
                  <select
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 outline-none focus:ring-1 focus:ring-amber-500"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={250}>250</option>
                    <option value={500}>500 (Semua)</option>
                  </select>
                  <span>data per halaman</span>
                </div>

                <div className="text-slate-500 font-medium">
                  Halaman {currentPage} dari {lastPage} (Total {totalItems} data Brankas Toko Pusat)
                </div>

                {lastPage > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      className="px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-600 transition-all"
                    >
                      Prev
                    </button>
                    <span className="px-3 py-1 font-bold text-amber-600 dark:text-amber-400">
                      {currentPage} / {lastPage}
                    </span>
                    <button
                      type="button"
                      disabled={currentPage === lastPage}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      className="px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-600 transition-all"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>

        {/* Modal Cetak Surat Jalan */}
        <SuratJalanPrint
          isOpen={isOpenPrint}
          closeModal={() => setIsOpenPrint(false)}
          data={createdData}
          breakdown={createdBreakdown}
        />

        {/* Modal Jenis Kategori */}
        {isOpenJenisKategoriModal && (
          <div className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 w-full max-w-md rounded-2xl p-6 shadow-2xl space-y-4 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3">
                <h3 className="font-bold text-sm text-slate-800 dark:text-slate-100">
                  Pilih Jenis Kategori
                </h3>
                <button type="button" onClick={() => { setIsOpenJenisKategoriModal(false); setSearchJenisKategori(''); }} className="text-slate-400 hover:text-slate-600">
                  <IconX size={18} />
                </button>
              </div>

              <div className="relative">
                <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Cari jenis kategori..."
                  value={searchJenisKategori}
                  onChange={(e) => setSearchJenisKategori(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900/50 outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-slate-700 dark:text-slate-200"
                />
              </div>

              <div className="max-h-60 overflow-y-auto space-y-1 pr-1">
                {(() => {
                  const jenisList = kategoriList.find(kt => kt.id === parseInt(selectedKategori))?.jenis_kategori || [];
                  const filtered = jenisList.filter(j => j.nama_jenis.toLowerCase().includes(searchJenisKategori.toLowerCase()));
                  
                  if (filtered.length === 0) {
                    return <div className="text-center text-xs text-slate-500 py-4">Tidak ada jenis kategori ditemukan</div>;
                  }

                  return (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedJenisKategori('');
                          setIsOpenJenisKategoriModal(false);
                          setSearchJenisKategori('');
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                          selectedJenisKategori === '' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 font-bold' : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                        }`}
                      >
                        Semua Jenis
                      </button>
                      {filtered.map(j => (
                        <button
                          key={j.id}
                          type="button"
                          onClick={() => {
                            setSelectedJenisKategori(j.id.toString());
                            setIsOpenJenisKategoriModal(false);
                            setSearchJenisKategori('');
                            setCurrentPage(1);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                            selectedJenisKategori === j.id.toString() ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 font-bold' : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                          }`}
                        >
                          {j.nama_jenis}
                        </button>
                      ))}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
        {/* Modal Detail Ringkasan Barang Dikeluarkan (Desain seperti Modal Detail Permohonan) */}
        {isOpenDetailModal && (
          <div
            className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-4 animate-fade-in"
            onClick={() => setIsOpenDetailModal(false)}
          >
            <div
              className="bg-white dark:bg-slate-800 w-full max-w-4xl lg:max-w-5xl rounded-2xl p-5 md:p-6 shadow-2xl border border-slate-200 dark:border-slate-700 max-h-[92vh] flex flex-col space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3 flex-shrink-0">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm md:text-base text-slate-800 dark:text-slate-100">
                      Detail Barang Dikeluarkan: <span className="font-mono text-amber-600">DRAFT-PENGIRIMAN</span>
                    </h3>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 rounded-md border border-amber-300 dark:border-amber-700 text-[11px] font-bold">
                      ● Siap Dikirim
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 mt-1">
                    <span><strong className="text-slate-600 dark:text-slate-400">Tanggal:</strong> {moment().format('DD MMM YYYY, HH:mm')}</span>
                    <span><strong className="text-slate-600 dark:text-slate-400">Asal:</strong> Brankas Toko Pusat (Rumah)</span>
                    {cabangTujuanId && (
                      <span><strong className="text-slate-600 dark:text-slate-400">Tujuan:</strong> {cabang.find(c => String(c.id) === String(cabangTujuanId))?.nama_cabang || '-'}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpenDetailModal(false)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Top Quick Info Strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-slate-50 dark:bg-slate-700/40 p-3 rounded-xl flex-shrink-0">
                <div>
                  <span className="text-slate-400 block text-[10px]">Toko Asal (Brankas)</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200 truncate block">
                    Brankas Toko Pusat (Rumah)
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">Toko Tujuan</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200 truncate block">
                    {cabang.find(c => String(c.id) === String(cabangTujuanId))?.nama_cabang || <span className="text-amber-500 italic">Belum dipilih</span>}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">Total Item & Berat</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200 truncate block">
                    {totalPcs} PCS
                    <span className="text-amber-600 dark:text-amber-400 font-semibold ml-1">
                      ({totalBerat.toFixed(3)} gr)
                    </span>
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">Kurir Pengantar</span>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400 truncate block">
                    {namaKurir || <span className="text-slate-400 italic">Belum diisi</span>}
                  </span>
                </div>
              </div>

              {/* Tab Navigation Bar */}
              <div className="flex border-b border-slate-200 dark:border-slate-700 gap-1 overflow-x-auto scrollbar-none flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setModalTab('ringkasan')}
                  className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                    modalTab === 'ringkasan'
                      ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
                >
                  <IconChartPie size={16} />
                  <span>Ringkasan Item</span>
                </button>
                <button
                  type="button"
                  onClick={() => setModalTab('list')}
                  className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                    modalTab === 'list'
                      ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
                >
                  <IconListDetails size={16} />
                  <span>List Item ({totalPcs})</span>
                </button>
                <button
                  type="button"
                  onClick={() => setModalTab('galeri')}
                  className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                    modalTab === 'galeri'
                      ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                  }`}
                >
                  <IconPhoto size={16} />
                  <span>Galeri List Item</span>
                </button>
              </div>

              {/* Scrollable Tab Content */}
              <div className="flex-1 overflow-y-auto pr-1">
                {/* 1. Tab Ringkasan Item */}
                {modalTab === 'ringkasan' && (() => {
                  const itemsList = selectedItemsList || [];
                  const totalPcsVal = totalPcs || itemsList.length;
                  const totalBeratVal = parseFloat(totalBerat || itemsList.reduce((acc, it) => acc + parseFloat(it.berat || it.item?.berat || 0), 0));
                  const avgBeratVal = itemsList.length > 0 ? (totalBeratVal / itemsList.length) : (totalPcsVal > 0 ? totalBeratVal / totalPcsVal : 0);
                  const validModalItems = itemsList.filter(it => Number(it.harga_modal || it.item?.harga_modal || 0) > 0);
                  const avgModalVal = validModalItems.length > 0
                    ? Math.round(validModalItems.reduce((acc, it) => acc + Number(it.harga_modal || it.item?.harga_modal || 0) / validModalItems.length))
                    : 0;
                  const validWeights = itemsList
                    .map(it => parseFloat(it.berat || it.item?.berat || 0) || 0)
                    .filter(w => w > 0);
                  const minBeratVal = validWeights.length > 0 ? Math.min(...validWeights) : 0;
                  const maxBeratVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => parseFloat(it.berat || it.item?.berat || 0) || 0)) : 0;
                  const minModalVal = validModalItems.length > 0
                    ? Math.min(...validModalItems.map(it => Number(it.harga_modal || it.item?.harga_modal || 0) || 0))
                    : 0;
                  const maxModalVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => Number(it.harga_modal || it.item?.harga_modal || 0) || 0)) : 0;

                  return (
                    <div className="space-y-4">
                      {/* Ringkasan Cards (9 Cards) */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-2.5 sm:gap-3">
                        <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                          <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Barang</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                            {totalPcsVal} <span className="text-xs font-semibold text-slate-500">Pcs</span>
                          </div>
                        </div>
                        <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                          <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Berat</span>
                          <div className="text-lg font-extrabold text-amber-600 dark:text-amber-400 mt-0.5 font-mono">
                            {totalBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Total Kategori</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                            {Object.keys(detailSummary).length} <span className="text-xs font-semibold text-slate-500">Kategori</span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Berat</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            {avgBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Berat Minimal</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            {minBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Berat Maksimal</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            {maxBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Modal</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                            Rp {avgModalVal.toLocaleString('id-ID')}
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Modal Minimal</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                            Rp {minModalVal.toLocaleString('id-ID')}
                          </div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Modal Maksimal</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                            Rp {maxModalVal.toLocaleString('id-ID')}
                          </div>
                        </div>
                      </div>

                    {/* Breakdown Kategori & Karat */}
                    {Object.keys(detailSummary).length > 0 && (
                      <div className="space-y-3 p-3.5 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-900/30 rounded-xl">
                        <div className="flex items-center justify-between">
                          <h4 className="text-[11px] font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                            <IconChartPie size={16} />
                            <span>Breakdown Per Kategori & Karat</span>
                          </h4>
                          <span className="text-[10px] text-amber-700 dark:text-amber-400 font-semibold">
                            {Object.keys(detailSummary).length} Kategori
                          </span>
                        </div>
                        <div className="space-y-2">
                          {Object.values(detailSummary).map((cat, idx) => (
                            <div key={idx} className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-amber-200/60 dark:border-slate-700 shadow-sm">
                              <div className="flex justify-between items-center mb-2 pb-2 border-b border-slate-100 dark:border-slate-700">
                                <span className="font-bold text-slate-800 dark:text-slate-100 text-sm">{cat.name}</span>
                                <span className="font-bold text-amber-600 dark:text-amber-400 text-xs bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-2.5 py-0.5 rounded-md">
                                  {cat.pcs} Pcs ({cat.berat.toFixed(3)} gr)
                                </span>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                {Object.entries(cat.byKarat).map(([karat, val], i) => (
                                  <div key={i} className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/40 px-2.5 py-1.5 rounded-lg text-xs border border-slate-100 dark:border-slate-700">
                                    <span className="font-semibold text-slate-500 dark:text-slate-400">{karat} Karat</span>
                                    <span className="font-bold text-slate-800 dark:text-slate-100">{val.pcs} Pcs / {val.berat.toFixed(3)}g</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Catatan Area */}
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
                      <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Catatan Pengiriman</span>
                      <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                        {catatanPengiriman || <span className="italic text-slate-400">Tidak ada catatan pengiriman khusus.</span>}
                      </p>
                    </div>
                  </div>
                );
              })()}

                {/* 2. Tab List Item (Tabel) */}
                {modalTab === 'list' && (
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="relative flex-1 max-w-xs">
                        <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                        <input
                          type="text"
                          placeholder="Cari kode, jenis, baki..."
                          value={searchModalItem}
                          onChange={(e) => setSearchModalItem(e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
                        />
                      </div>
                      <span className="text-[11px] text-slate-500 font-medium">
                        Menampilkan {filteredModalItems.length} dari {totalPcs} item terpilih
                      </span>
                    </div>

                    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto max-h-80">
                      <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                        <thead className="bg-gradient-to-r from-amber-50 via-amber-100/80 to-amber-50 dark:from-amber-900/40 dark:via-amber-800/30 dark:to-amber-900/40 text-[11px] text-amber-800 dark:text-amber-300 uppercase border-b border-amber-200/70 dark:border-amber-800/40 sticky top-0 z-10 font-bold tracking-wider">
                          <tr>
                            <th className="p-2.5 w-10 text-center font-bold">No</th>
                            <th className="p-2.5 font-bold">Kode Barang</th>
                            <th className="p-2.5 font-bold">Nama Barang / Jenis</th>
                            <th className="p-2.5 text-center font-bold">Baki / Rak</th>
                            <th className="p-2.5 text-right font-bold">Berat (gr)</th>
                            <th className="p-2.5 text-center font-bold">Karat</th>
                            <th className="p-2.5 text-center font-bold">Aksi</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                          {filteredModalItems.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center py-6 text-slate-400">
                                Tidak ada item yang sesuai dengan pencarian
                              </td>
                            </tr>
                          ) : (
                            filteredModalItems.map((it, idx) => (
                              <tr key={it.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                                <td className="p-2.5 text-slate-500 font-medium text-center">{idx + 1}.</td>
                                <td className="p-2.5 font-mono font-bold text-amber-600 dark:text-amber-400">{it.kode_item}</td>
                                <td className="p-2.5">
                                  <span className="font-semibold text-slate-700 dark:text-slate-200 block">
                                    {it.jenis_kategori?.kategori?.nama_kategori || '-'} - {it.jenis_kategori?.nama_jenis || '-'}
                                  </span>
                                </td>
                                <td className="p-2.5 text-center font-medium">{it.nomor_rak || '-'}</td>
                                <td className="p-2.5 text-right font-bold text-amber-600 dark:text-amber-400">{parseFloat(it.berat).toFixed(3)} gr</td>
                                <td className="p-2.5 text-center font-semibold">{it.karat} K</td>
                                <td className="p-2.5 text-center">
                                  <button
                                    type="button"
                                    onClick={() => handleToggleSelectItem(it)}
                                    className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-md transition-colors"
                                    title="Hapus dari daftar kirim"
                                  >
                                    <IconX size={15} />
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                        {filteredModalItems.length > 0 && (
                          <tfoot className="bg-slate-50 dark:bg-slate-700/50 font-bold border-t border-slate-200 dark:border-slate-700 sticky bottom-0">
                            <tr>
                              <td colSpan={4} className="p-2.5 text-right text-slate-600 dark:text-slate-300">
                                Total ({filteredModalItems.length} item):
                              </td>
                              <td className="p-2.5 text-right text-amber-600 dark:text-amber-400 font-mono">
                                {filteredModalItems.reduce((acc, it) => acc + parseFloat(it.berat || 0), 0).toFixed(3)} gr
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>
                )}

                {/* 3. Tab Galeri List Item */}
                {modalTab === 'galeri' && (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="relative flex-1 max-w-xs">
                        <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                        <input
                          type="text"
                          placeholder="Cari kode, jenis, baki..."
                          value={searchModalItem}
                          onChange={(e) => setSearchModalItem(e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
                        />
                      </div>
                      <span className="text-[11px] text-slate-500 font-medium">
                        Menampilkan {filteredModalItems.length} foto item
                      </span>
                    </div>

                    {filteredModalItems.length === 0 ? (
                      <div className="py-12 text-center text-slate-400">
                        <IconInbox size={36} className="mx-auto mb-2 opacity-50" />
                        <p className="text-xs">Tidak ada item yang sesuai dengan pencarian</p>
                      </div>
                    ) : (
                      <PhotoProvider>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {filteredModalItems.map((item) => {
                            const imgUrl = resolveItemImageUrl(item);
                            return (
                              <div
                                key={item.id}
                                className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow group flex flex-col"
                              >
                                <div className="aspect-square bg-slate-100 dark:bg-slate-700/50 relative flex items-center justify-center overflow-hidden">
                                  {imgUrl ? (
                                    <PhotoView src={imgUrl}>
                                      <img
                                        src={imgUrl}
                                        alt={item.kode_item}
                                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </PhotoView>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center text-slate-400 p-2 text-center">
                                      <IconPhoto size={28} className="opacity-40 mb-1" />
                                      <span className="text-[10px]">Belum Ada Gambar</span>
                                    </div>
                                  )}
                                  <div className="absolute top-2 left-2 bg-slate-900/80 backdrop-blur-sm text-amber-400 text-[10px] font-mono font-bold px-2 py-0.5 rounded shadow">
                                    {item.kode_item}
                                  </div>
                                  <div className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow">
                                    {item.karat} K
                                  </div>
                                </div>

                                <div className="p-2.5 text-xs flex-1 flex flex-col justify-between space-y-2">
                                  <div>
                                    <p className="font-bold text-slate-800 dark:text-slate-100 text-[11px] truncate" title={`${item.jenis_kategori?.kategori?.nama_kategori || ''} - ${item.jenis_kategori?.nama_jenis || ''}`}>
                                      {item.jenis_kategori?.kategori?.nama_kategori || '-'} - {item.jenis_kategori?.nama_jenis || '-'}
                                    </p>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                      Baki: <span className="font-semibold text-slate-700 dark:text-slate-300">{item.nomor_rak || '-'}</span>
                                    </p>
                                  </div>
                                  <div className="flex items-center justify-between pt-1.5 border-t border-slate-100 dark:border-slate-700">
                                    <span className="text-[10px] text-slate-400">Berat</span>
                                    <span className="font-bold text-amber-600 dark:text-amber-400 text-xs">
                                      {parseFloat(item.berat || 0).toFixed(3)} gr
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </PhotoProvider>
                    )}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex flex-col-reverse sm:flex-row justify-between items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-700 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setIsOpenDetailModal(false)}
                  className="w-full sm:w-auto px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all"
                >
                  Tutup
                </button>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpenDetailModal(false);
                      handlePreSubmit({ preventDefault: () => {} });
                    }}
                    disabled={isSubmitting || totalPcs === 0}
                    className={`w-full sm:w-auto px-4 py-2 rounded-xl font-bold text-xs shadow-md transition-all flex items-center justify-center gap-2 ${
                      isSubmitting || totalPcs === 0
                        ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-amber-500/25 cursor-pointer'
                    }`}
                  >
                    <IconPrinter size={16} />
                    <span>Buat Surat Jalan & Cetak</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Modern Confirmation Modal with 3 Buttons */}
      {isConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-700">
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/30 text-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                <IconPrinter size={30} />
              </div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2">
                Konfirmasi Buat Surat Jalan
              </h3>
              <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                Apakah Anda yakin ingin membuat Surat Jalan untuk <strong className="text-slate-800 dark:text-white font-bold">{totalPcs} barang</strong> dengan total berat <strong className="text-amber-600 dark:text-amber-400 font-mono font-bold">{totalBerat.toFixed(3)} gr</strong> ke kurir <strong className="text-slate-800 dark:text-white font-bold">{namaKurir}</strong>?
              </p>
              <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Toko Tujuan: <strong className="text-slate-700 dark:text-slate-200 font-semibold">{cabang.find(c => String(c.id) === String(cabangTujuanId))?.nama_cabang || '-'}</strong>
              </div>
            </div>

            {/* Penjelasan 2 Jalur Approval */}
            <div className="px-6 pb-2 space-y-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Pilih Jalur Proses Pengiriman:
              </div>

              {/* Jalur 1: Standar */}
              <div className="p-2.5 rounded-xl border border-amber-200/80 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-950/20 flex items-start gap-2.5">
                <span className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0"></span>
                <div className="text-left text-xs">
                  <span className="font-bold text-amber-900 dark:text-amber-300">1. Buat Surat Jalan (Standar):</span>
                  <p className="text-slate-600 dark:text-slate-400 text-[11px] mt-0.5">
                    Harus melewati approve di toko tujuan setelah barang diterima oleh operator/kepala toko.
                  </p>
                </div>
              </div>

              {/* Jalur 2: Tanpa Verifikasi */}
              <div className="p-2.5 rounded-xl border border-emerald-300/80 dark:border-emerald-800/40 bg-emerald-50/70 dark:bg-emerald-950/20 flex items-start gap-2.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0"></span>
                <div className="text-left text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-emerald-900 dark:text-emerald-300">2. Buat Surat Jalan (Tanpa Verifikasi):</span>
                    <span className="px-1.5 py-0.2 bg-emerald-200 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 text-[9px] font-extrabold rounded">
                      Auto-Approve
                    </span>
                  </div>
                  <p className="text-slate-600 dark:text-slate-400 text-[11px] mt-0.5">
                    Langsung diapprove oleh pembuat (owner/admin) tanpa melewati antrean approve di akun operator toko.
                  </p>
                </div>
              </div>
            </div>

            {/* Indikator Loading Status Submit */}
            {isSubmitting && (
              <div className="mx-6 mb-3 p-3.5 rounded-xl bg-amber-50/90 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 flex items-center gap-3 animate-pulse shadow-sm">
                <span className="loading loading-spinner loading-md text-amber-600 dark:text-amber-400 flex-shrink-0"></span>
                <div className="text-xs text-amber-900 dark:text-amber-200 text-left">
                  <p className="font-bold text-[13px]">
                    {submittingType === 'tanpa_verifikasi'
                      ? 'Sedang membuat Surat Jalan & memproses Auto-Approve...'
                      : 'Sedang membuat Surat Jalan (Standar)...'}
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    Mohon tunggu sebentar, sistem sedang memproses pengiriman sebelum menampilkan surat jalan...
                  </p>
                </div>
              </div>
            )}

            {/* 3 Action Buttons */}
            <div className="bg-slate-50 dark:bg-slate-700/50 px-6 py-4 flex flex-col sm:flex-row gap-2.5 justify-end items-center border-t border-slate-100 dark:border-slate-700">
              {/* Button 1: Batal */}
              <button
                type="button"
                onClick={() => !isSubmitting && setIsConfirmOpen(false)}
                disabled={isSubmitting}
                className={`w-full sm:w-auto px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-xl font-bold transition-all text-xs order-3 sm:order-1 ${
                  isSubmitting
                    ? 'opacity-40 cursor-not-allowed'
                    : 'hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer'
                }`}
              >
                Batal
              </button>

              {/* Button 2: Buat Surat Jalan (Amber) */}
              <button
                type="button"
                onClick={() => handleSubmitSuratJalan(false)}
                disabled={isSubmitting}
                className={`w-full sm:w-auto px-4 py-2.5 rounded-xl font-bold text-xs shadow-md transition-all flex items-center justify-center gap-1.5 order-2 sm:order-2 ${
                  isSubmitting
                    ? submittingType === 'standar'
                      ? 'bg-amber-600 text-white cursor-wait'
                      : 'bg-amber-300 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 opacity-50 cursor-not-allowed'
                    : 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/25 cursor-pointer'
                }`}
              >
                {isSubmitting && submittingType === 'standar' ? (
                  <>
                    <IconLoader2 size={16} className="animate-spin" />
                    <span>Memproses Surat Jalan...</span>
                  </>
                ) : (
                  <>
                    <IconPrinter size={16} />
                    <span>Buat Surat Jalan</span>
                  </>
                )}
              </button>

              {/* Button 3: Buat Surat Jalan (Tanpa Verifikasi) (Emerald + Indikator Khusus Bolt) */}
              <button
                type="button"
                onClick={() => handleSubmitSuratJalan(true)}
                disabled={isSubmitting}
                className={`w-full sm:w-auto px-4 py-2.5 rounded-xl font-bold text-xs shadow-lg transition-all flex items-center justify-center gap-2 relative overflow-hidden order-1 sm:order-3 ring-2 ring-emerald-500/40 ${
                  isSubmitting
                    ? submittingType === 'tanpa_verifikasi'
                      ? 'bg-emerald-700 text-white cursor-wait'
                      : 'bg-emerald-300 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-400 opacity-50 cursor-not-allowed'
                    : 'bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 hover:from-emerald-700 hover:to-teal-700 text-white shadow-emerald-600/30 cursor-pointer'
                }`}
                title="Langsung diapprove tanpa melewati antrean approve di toko tujuan"
              >
                {isSubmitting && submittingType === 'tanpa_verifikasi' ? (
                  <>
                    <IconLoader2 size={16} className="animate-spin" />
                    <span>Memproses Auto-Approve...</span>
                  </>
                ) : (
                  <>
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/30 text-amber-300 border border-amber-300/40">
                      <IconBolt size={14} className="animate-pulse" />
                    </span>
                    <span>Buat Surat Jalan (Tanpa Verifikasi)</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layouts>
  );
};

export default CreateSuratJalan;
